class JenyasTheme {
  constructor() {
    this.root = document.documentElement;
    this.megaRoot = document.querySelector('[data-mega-root]');
    this.megaTrigger = document.querySelector('[data-mega-trigger]');
    this.megaMenu = document.querySelector('[data-mega-menu]');
    this.drawer = document.querySelector('[data-menu-drawer]');
    this.searchPanel = document.querySelector('[data-header-search]');
    this.searchButton = document.querySelector('[data-search-open]');
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchResults = document.querySelector('[data-search-results]');
    this.searchDefaultResults = this.searchResults?.innerHTML || '';
    this.searchTimer = null;
    this.searchController = null;
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartBody = document.querySelector('[data-cart-drawer-body]');
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.discountPopup = document.querySelector('[data-discount-popup]');
    this.megaCloseTimer = null;
    this.bindNavigation();
    this.bindCartDrawer();
    this.bindDiscountPopup();
    this.bindShoppableRails();
    this.bindTriptychs();
    this.bindGalleries();
    this.bindProductGalleryScroll();
    this.bindQuantity();
    this.bindProductForms();
    this.bindProductRecommendations();
  }

  setMega(open) {
    if (!this.megaMenu) return;
    window.clearTimeout(this.megaCloseTimer);
    this.megaMenu.classList.toggle('open', open);
    this.megaTrigger?.setAttribute('aria-expanded', String(open));
    this.megaMenu.setAttribute('aria-hidden', String(!open));
  }

  setDrawer(open) {
    this.drawer?.classList.toggle('open', open);
    this.overlay?.classList.toggle('visible', open);
    this.drawer?.setAttribute('aria-hidden', String(!open));
    this.root.classList.toggle('drawer-open', open);
  }

  setSearch(open) {
    this.searchPanel?.classList.toggle('open', open);
    this.searchPanel?.setAttribute('aria-hidden', String(!open));
    this.searchButton?.setAttribute('aria-expanded', String(open));
    if (open) window.setTimeout(() => this.searchInput?.focus(), 80);
    if (!open) {
      if (this.searchInput) this.searchInput.value = '';
      this.renderSearchDefault();
    }
  }

  setCart(open) {
    this.cartDrawer?.classList.toggle('open', open);
    this.overlay?.classList.toggle('visible', open);
    this.cartDrawer?.setAttribute('aria-hidden', String(!open));
    this.root.classList.toggle('drawer-open', open);
  }

  setDiscountPopup(open) {
    if (!this.discountPopup) return;
    this.discountPopup.classList.toggle('open', open);
    this.discountPopup.setAttribute('aria-hidden', String(!open));
    this.root.classList.toggle('drawer-open', open);
  }

  closeMegaSoon() {
    window.clearTimeout(this.megaCloseTimer);
    this.megaCloseTimer = window.setTimeout(() => this.setMega(false), 700);
  }

  bindNavigation() {
    this.megaRoot?.addEventListener('mouseenter', () => this.setMega(true));
    this.megaRoot?.addEventListener('mouseleave', () => this.closeMegaSoon());
    this.megaMenu?.addEventListener('mouseenter', () => this.setMega(true));
    this.megaMenu?.addEventListener('mouseleave', () => this.closeMegaSoon());
    this.megaTrigger?.addEventListener('click', () => this.setMega(this.megaTrigger.getAttribute('aria-expanded') !== 'true'));
    document.querySelector('[data-menu-open]')?.addEventListener('click', () => this.setDrawer(true));
    document.querySelector('[data-menu-close]')?.addEventListener('click', () => this.setDrawer(false));
    this.searchButton?.addEventListener('click', () => this.setSearch(this.searchButton.getAttribute('aria-expanded') !== 'true'));
    document.querySelector('[data-search-close]')?.addEventListener('click', () => this.setSearch(false));
    this.overlay?.addEventListener('click', () => { this.setDrawer(false); this.setSearch(false); this.setCart(false); });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { this.setMega(false); this.setDrawer(false); this.setSearch(false); this.setCart(false); this.setDiscountPopup(false); }
    });
    this.searchPanel?.addEventListener('submit', (event) => {
      event.preventDefault();
      const firstResult = this.searchResults?.querySelector('[data-search-result]');
      if (firstResult) {
        window.location.href = firstResult.href;
        return;
      }
      this.fetchSearchSuggestions(this.searchInput?.value || '');
    });
    this.searchInput?.addEventListener('input', () => {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.fetchSearchSuggestions(this.searchInput.value), 180);
    });
    document.addEventListener('click', (event) => {
      if (!this.searchPanel?.classList.contains('open')) return;
      if (event.target.closest('[data-header-search], [data-search-open]')) return;
      this.setSearch(false);
    });
  }

  escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value || '';
    return element.innerHTML;
  }

  renderSearchDefault() {
    if (!this.searchResults) return;
    this.searchController?.abort();
    this.searchResults.innerHTML = this.searchDefaultResults;
  }

  renderSearchSuggestions(products, term) {
    if (!this.searchResults) return;
    if (!products.length) {
      this.searchResults.innerHTML = `
        <span class="header-search-label">No products found</span>
        <p class="header-search-empty">Try rings, pendants, moissanite, amber, or sterling silver.</p>
      `;
      return;
    }
    this.searchResults.innerHTML = `
      <span class="header-search-label">Suggestions for ${this.escapeHtml(term)}</span>
      <div class="header-search-list">
        ${products.map((product) => {
          const image = typeof product.image === 'string' ? product.image : product.image?.url || product.featured_image?.url || '';
          const price = product.price || product.price_min || '';
          return `
            <a class="header-search-result" data-search-result href="${this.escapeHtml(product.url)}">
              <span>${image ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}</span>
              <strong>${this.escapeHtml(product.title)}</strong>
              ${price ? `<small>${this.escapeHtml(price)}</small>` : ''}
            </a>
          `;
        }).join('')}
      </div>
    `;
  }

  async fetchSearchSuggestions(term) {
    const query = term.trim();
    if (!query) {
      this.renderSearchDefault();
      return;
    }
    this.searchController?.abort();
    this.searchController = new AbortController();
    try {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor'
      });
      const response = await fetch(`/search/suggest.json?${params}`, {
        headers: { Accept: 'application/json' },
        signal: this.searchController.signal
      });
      if (!response.ok) throw new Error('Search suggestions failed');
      const data = await response.json();
      this.renderSearchSuggestions(data.resources?.results?.products || [], query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.searchResults) this.searchResults.innerHTML = '<span class="header-search-label">Search is loading slowly</span><p class="header-search-empty">Please try again in a moment.</p>';
    }
  }

  bindProductRecommendations() {
    if (customElements.get('product-recommendations')) return;
    customElements.define('product-recommendations', class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true' || !this.dataset.url) return;
        this.dataset.loaded = 'true';
        const limit = Number(this.dataset.limit) || 10;
        const productKey = (card) => card.querySelector('.product-image-link, .product-meta a')?.getAttribute('href') || card.textContent.trim();
        const fetchCards = (url) => fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            return [...html.querySelectorAll('product-recommendations .product-card')];
          });
        fetchCards(this.dataset.url)
          .then((cards) => {
            const seen = new Set(cards.map(productKey));
            const finish = (allCards) => {
              if (!allCards.length) {
                this.closest('.pdp-related')?.setAttribute('hidden', 'hidden');
                return;
              }
              const grid = document.createElement('div');
              grid.className = 'pdp-related-grid';
              allCards.slice(0, limit).forEach((card) => grid.append(card));
              this.replaceChildren(grid);
            };
            if (!this.dataset.fallbackUrl || cards.length >= limit) {
              finish(cards);
              return;
            }
            fetchCards(this.dataset.fallbackUrl).then((fallbackCards) => {
              fallbackCards.forEach((card) => {
                const key = productKey(card);
                if (!seen.has(key) && cards.length < limit) {
                  seen.add(key);
                  cards.push(card);
                }
              });
              finish(cards);
            }).catch(() => finish(cards));
          })
          .catch(() => {
            this.closest('.pdp-related')?.setAttribute('hidden', 'hidden');
          });
      }
    });
  }

  discountStorage() {
    if (!this.discountPopup) return null;
    const frequency = this.discountPopup.dataset.frequency || 'session';
    if (frequency === 'always') return null;
    return frequency === 'once' ? window.localStorage : window.sessionStorage;
  }

  bindDiscountPopup() {
    if (!this.discountPopup) return;
    const designMode = this.discountPopup.dataset.designMode === 'true';
    if (this.discountPopup.dataset.enabled !== 'true' && !designMode) return;
    const storage = this.discountStorage();
    const key = 'jenyas-discount-popup-seen';
    const params = new URLSearchParams(window.location.search);
    const successPanel = this.discountPopup.querySelector('[data-discount-instant-success]');
    const formFields = this.discountPopup.querySelector('[data-discount-form-fields]');
    const serverSuccess = Boolean(this.discountPopup.querySelector('.discount-popup-success:not([hidden])'));
    const submitted = serverSuccess || params.get('discount_popup') === 'success' || params.get('customer_posted') === 'true';
    const markSeen = () => {
      try { storage?.setItem(key, 'true'); } catch (error) { return; }
    };
    const shouldShow = () => {
      if (designMode) return true;
      try { return !storage?.getItem(key); } catch (error) { return true; }
    };
    const close = () => { markSeen(); this.setDiscountPopup(false); };
    this.discountPopup.querySelectorAll('[data-discount-close]').forEach((button) => button.addEventListener('click', close));
    if (submitted) {
      successPanel?.removeAttribute('hidden');
      formFields?.setAttribute('hidden', 'hidden');
      this.setDiscountPopup(true);
      markSeen();
      params.delete('discount_popup');
      params.delete('customer_posted');
      const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', cleanUrl);
      return;
    }
    if (shouldShow()) {
      window.setTimeout(() => this.setDiscountPopup(true), Number(this.discountPopup.dataset.delay) || 0);
    }
  }

  bindShoppableRails(scope = document) {
    scope.querySelectorAll('[data-shoppable-rail]').forEach((rail) => {
      if (rail.dataset.ready === 'true') return;
      rail.dataset.ready = 'true';
      let paused = false;
      const interval = Number(rail.dataset.interval) || 3000;
      const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
      const nextPage = () => {
        if (paused || document.hidden || maxScroll() < 8) return;
        const step = rail.clientWidth;
        const target = rail.scrollLeft + step >= maxScroll() - 8 ? 0 : rail.scrollLeft + step;
        rail.scrollTo({ left: target, behavior: 'smooth' });
      };
      const pause = () => { paused = true; };
      const play = () => { paused = false; };
      rail.addEventListener('mouseenter', pause);
      rail.addEventListener('mouseleave', play);
      rail.addEventListener('focusin', pause);
      rail.addEventListener('focusout', play);
      rail.addEventListener('pointerdown', pause);
      rail.addEventListener('pointerup', play);
      rail.addEventListener('pointercancel', play);
      window.setInterval(nextPage, interval);
    });
  }

  bindCartDrawer() {
    document.querySelectorAll('.bag-button').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      this.refreshCart().then(() => this.setCart(true));
    }));
    document.querySelector('[data-cart-close]')?.addEventListener('click', () => this.setCart(false));
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-cart-add]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      button?.setAttribute('disabled', 'disabled');
      try {
        await fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        await this.refreshCart();
        this.setCart(true);
      } finally {
        button?.removeAttribute('disabled');
      }
    });
  }

  money(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  async refreshCart() {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    document.querySelectorAll('[data-cart-count]').forEach((item) => { item.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-subtotal]').forEach((item) => { item.textContent = this.money(cart.total_price); });
    if (!this.cartBody) return cart;
    this.cartBody.innerHTML = cart.items.length ? cart.items.map((item) => `
      <article class="cart-drawer-item">
        <a href="${item.url}" class="cart-drawer-media">${item.image ? `<img src="${item.image}" alt="${item.product_title}">` : ''}</a>
        <div>
          <a href="${item.url}">${item.product_title}</a>
          <span>${this.money(item.final_line_price)}</span>
          <small>Qty ${item.quantity}</small>
        </div>
      </article>
    `).join('') : `
      <div class="cart-drawer-empty">
        <span>Your cart is empty</span>
        <p>Start with sculptural silver, turquoise, or a giftable everyday piece.</p>
      </div>
    `;
    return cart;
  }

  bindGalleries() {
    document.querySelectorAll('[data-gallery-thumb]').forEach((button) => button.addEventListener('click', () => {
      const gallery = button.closest('[data-product-gallery]');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.remove('active'));
      gallery.querySelectorAll('[data-gallery-media]').forEach((item) => item.classList.remove('active'));
      button.classList.add('active');
      gallery.querySelector(`[data-gallery-media="${button.dataset.galleryThumb}"]`)?.classList.add('active');
    }));
  }

  bindProductGalleryScroll() {
    const main = document.querySelector('.shopify-product-page .pdp-main');
    const gallery = main?.querySelector('[data-product-gallery]');
    if (!main || !gallery) return;

    const reset = () => {
      gallery.style.transform = '';
    };

    const update = () => {
      if (window.matchMedia('(max-width: 760px)').matches) {
        reset();
        return;
      }

      const mainRect = main.getBoundingClientRect();
      const topOffset = 18;
      const startTop = mainRect.top + window.scrollY;
      const galleryHeight = gallery.offsetHeight;
      const maxTravel = Math.max(0, main.offsetHeight - galleryHeight);
      const travel = Math.min(Math.max(0, window.scrollY - startTop + topOffset), maxTravel);

      gallery.style.transform = travel > 0 ? `translate3d(0, ${travel}px, 0)` : '';
    };

    let ticking = false;
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    gallery.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', requestUpdate, { once: true });
    });
    update();
  }

  bindTriptychs(scope = document) {
    scope.querySelectorAll('[data-triptych]').forEach((triptych) => {
      if (triptych.dataset.ready === 'true') return;
      triptych.dataset.ready = 'true';
      const panels = [...triptych.querySelectorAll('[data-triptych-panel]')];
      const progress = [...triptych.querySelectorAll('[data-triptych-progress]')];
      if (!panels.length) return;
      const duration = Number(triptych.dataset.duration) || 4000;
      let active = Math.max(0, panels.findIndex((panel) => panel.classList.contains('active')));
      triptych.style.setProperty('--triptych-duration', `${duration}ms`);

      const activate = (index) => {
        active = index;
        panels.forEach((panel, panelIndex) => panel.classList.toggle('active', panelIndex === active));
        progress.forEach((item, itemIndex) => item.classList.toggle('active', itemIndex === active));
      };

      activate(active);
      window.setInterval(() => activate((active + 1) % panels.length), duration);
    });
  }

  bindQuantity() {
    document.querySelectorAll('[data-quantity]').forEach((control) => {
      const input = control.querySelector('input');
      control.querySelector('[data-minus]')?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) - 1); input.dispatchEvent(new Event('change', {bubbles: true})); });
      control.querySelector('[data-plus]')?.addEventListener('click', () => { input.value = Number(input.value) + 1; input.dispatchEvent(new Event('change', {bubbles: true})); });
    });
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const select = form.querySelector('[data-variant-select]');
      select?.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        document.querySelectorAll('[data-current-price]').forEach((el) => { el.textContent = option.dataset.price; });
        form.querySelectorAll('[data-variant-id]').forEach((el) => { el.value = option.value; });
        form.querySelectorAll('[data-add-button]').forEach((el) => { el.disabled = option.dataset.available !== 'true'; });
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new JenyasTheme());
